import { type FC, type ReactNode, memo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import type { MappingPagedCollectionOfEventSearchResultAndEventGridItem } from "src/http/types";
import { Content } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useCreateQueryFn } from "src/shared/hooks/useCreateQueryFn";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { useIsMobileClientApp } from "src/shared/utils/appContextHelper";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import {
  exponentialBackoffRetryDelay,
  FIVE_RETRIES,
  STALE_TIME_ONE_HOUR,
} from "src/shared/utils/query/retry-constants";
import type { Dates, Location } from "src/types/shared/cookies";
import { CarouselId } from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { ClickableLocationLink } from "src/website-apps/shared/components/events-grid/events-grid-title/ClickableLocationLink";
import { GenericEventCarousel } from "src/website-apps/shared/components/GenericEventCarousel";
import { ImageSize } from "src/website-apps/shared/components/image";
import { LoadingCarousel } from "src/website-apps/shared/components/LoadingCarousel";
import { ColoredLocationTextWrapper } from "src/website-apps/shared/components/trending-events-locale-carousel-grid/styled-components";
import { TRENDING_EVENTS_CAROUSEL_KEY } from "src/website-apps/shared/constants/carousel-keys";

const getShortenedLocation = (userLocation: string | undefined) => (userLocation ?? "").split(",", 1)[0] ?? "";

interface Props {
  categoryId?: number;
  dates?: Maybe<Dates>;
  /** When provided, excludes events belonging to this category from results. Used on residency pages to filter out the current performer. */
  excludeCategoryId?: number | undefined;
  initialData?: Maybe<MappingPagedCollectionOfEventSearchResultAndEventGridItem>;
  isLoading?: boolean;
  analyticsCategoryId?: number | undefined;
  /** When provided, overrides userLocation for the query and query key. Used on residency pages to scope to venue city. */
  overrideLocation?: Pick<Location, "lt" | "lg" | "n"> | undefined;
  /** When provided, replaces the default "Trending events near <city>" title. */
  title?: ReactNode | undefined;
  userLocation: Maybe<Location>;
  /** When true, renders a clickable location link instead of static text in the default title. */
  useClickableLocation?: boolean;
}

/**
 * Trending Events in Locale carousel
 */
export const TrendingEventsCarousel: FC<Props> = memo(
  ({
    categoryId = 0,
    dates,
    excludeCategoryId,
    initialData = null,
    isLoading = false,
    analyticsCategoryId,
    overrideLocation,
    title: titleProp,
    userLocation,
    useClickableLocation = false,
  }) => {
    const createQueryFn = useCreateQueryFn();
    const storePageEvent = useStorePageEvent();
    const effectiveLocation = overrideLocation ?? userLocation;
    const { lt, lg, n } = effectiveLocation ?? {};
    const { from, to } = dates?.dates ?? {};
    const userLocationTitle = getShortenedLocation(n);
    const totalItemCount = 10;
    const isMobile = !useMediaQuery(Breakpoint.Tablet);

    /**
     * Retrieves trending events. Residency callers pass no dates because the performer date
     * selector does not affect this carousel's data.
     */
    const fromKey = from ? (typeof from === "string" ? from : from.toJSON()) : "";
    const toKey = to ? (typeof to === "string" ? to : to.toJSON()) : "";

    const { data: events, isLoading: pending } = useQuery({
      queryKey: createQueryKey(QueryKeyPrefix.TRENDING_EVENTS, {
        categoryId,
        venueId: null,
        lt: lt ?? 0,
        lg: lg ?? 0,
        fromKey,
        toKey,
        excludeCategoryId: excludeCategoryId ?? 0,
      }),
      queryFn: async (context) => {
        const fromValue = from ? (typeof from === "string" ? from : from.toJSON()) : undefined;
        const toValue = to ? (typeof to === "string" ? to : to.toJSON()) : undefined;

        return await createQueryFn<MappingPagedCollectionOfEventSearchResultAndEventGridItem>({
          method: "TrendingEventsLocale",
          params: [
            ["categoryId", categoryId],
            ["maxRows", totalItemCount * 2],
            ["page", 0],
            ...(fromValue ? [["from", fromValue] as [string, string]] : []),
            ...(toValue ? [["to", toValue] as [string, string]] : []),
            ...(overrideLocation
              ? ([
                  ["lt", overrideLocation.lt],
                  ["lg", overrideLocation.lg],
                ] as [string, number][])
              : []),
            ...(excludeCategoryId ? ([["excludeCategoryId", excludeCategoryId]] as [string, number][]) : []),
          ],
        })(context);
      },
      // When overrideLocation is provided (residency), enable as long as coords are valid.
      // Otherwise, require user location + a selected date range (existing behaviour).
      enabled: Boolean(overrideLocation ? overrideLocation.lt && overrideLocation.lg : lt && lg && from),
      ...(initialData && { initialData }),
      // When excludeCategoryId is set, the initialData came from the batch fetch which
      // didn't apply the exclusion. Mark it as instantly stale so React Query refetches
      // immediately with the correct excludeCategoryId param instead of caching for staleTime.
      ...(initialData && excludeCategoryId && { initialDataUpdatedAt: 0 }),
      staleTime: STALE_TIME_ONE_HOUR,
      retry: FIVE_RETRIES,
      retryDelay: (attemptIndex) => {
        storePageEvent(PE_TYPE.LOGGING, PE_KEYS.TRENDING_CAROUSEL_FETCH_RETRY_REQUEST, `ERROR-${attemptIndex + 1}`);
        return exponentialBackoffRetryDelay(attemptIndex);
      },
    });

    const defaultTitle = (
      <>
        {/* "Trending events near" */}
        <Content id="cid$#3672334#" />
        {useClickableLocation ? (
          <ClickableLocationLink />
        ) : (
          !!userLocationTitle && (
            <>
              {" "}
              <ColoredLocationTextWrapper ancillaryPage>{userLocationTitle}</ColoredLocationTextWrapper>
            </>
          )
        )}
      </>
    );
    const title = titleProp ?? defaultTitle;

    // MHomeReduceEventCardSize (23424): deployed variant targeted mobile client apps only.
    const reduceEventCardSizes = useIsMobileClientApp();
    const imageSize = reduceEventCardSizes ? ImageSize.Medium4x3 : ImageSize.MediumCarouselImage;

    const loading = pending || isLoading;
    if (loading) {
      return <LoadingCarousel size={imageSize} count={isMobile ? 2 : 4} placeHolderLineCount={3} />;
    }

    return (
      <GenericEventCarousel
        carouselData={events ?? null}
        isLoading={loading}
        carouselKey={TRENDING_EVENTS_CAROUSEL_KEY}
        title={title}
        showRanking
        refreshDependencies={events ? [events] : []}
        analyticsCarouselId={CarouselId.TrendingEvents}
        analyticsCategoryId={analyticsCategoryId}
      />
    );
  },
);
TrendingEventsCarousel.displayName = "TrendingEventsCarousel";
